import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Users, 
    Plus,
    Trash2,
    Loader2,
    Mail,
    Shield,
    Copy,
    CheckCircle2,
    Tag,
    KeyRound,
    Eye,
    EyeOff,
    RefreshCw,
    Building2,
    Search,
    Filter
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const USER_TAG_OPTIONS = [
    { id: 'Sync-Artist', label: 'Sync Artist' },
    { id: 'Sync-Producer', label: 'Sync Producer' },
    { id: 'Sync-Reviewer', label: 'Sync Reviewer' },
    { id: 'Sync-Poster', label: 'Sync Poster' }
];

// Generate a random password
const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
};

export default function AdminUsersPage() {
    const { token, user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    
    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Reset password modal state
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [userToReset, setUserToReset] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [resetting, setResetting] = useState(false);
    
    const [newUser, setNewUser] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'submitter',
        user_tags: [],
        company_name: '',
        company_website: ''
    });

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${API}/admin/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                toast.error('Failed to load users');
            }
        } catch (error) {
            toast.error('Error loading users');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        
        if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
            toast.error('Please fill in all fields');
            return;
        }

        setCreating(true);
        try {
            const response = await fetch(`${API}/auth/register`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newUser)
            });

            if (response.ok) {
                toast.success('User created successfully! GHL contact will be synced.');
                setDialogOpen(false);
                setNewUser({
                    email: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    role: 'submitter',
                    user_tags: [],
                    company_name: '',
                    company_website: ''
                });
                fetchUsers();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to create user');
            }
        } catch (error) {
            toast.error('Error creating user');
        } finally {
            setCreating(false);
        }
    };

    const toggleTag = (tagId) => {
        setNewUser(prev => ({
            ...prev,
            user_tags: prev.user_tags.includes(tagId)
                ? prev.user_tags.filter(t => t !== tagId)
                : [...prev.user_tags, tagId]
        }));
    };

    const openDeleteModal = (user) => {
        // Prevent deleting yourself
        if (user.email === currentUser?.email) {
            toast.error("You cannot delete your own account");
            return;
        }
        setUserToDelete(user);
        setDeleteModalOpen(true);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        
        setDeleting(true);
        try {
            const response = await fetch(`${API}/admin/users/${userToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('User deleted successfully');
                setUsers(users.filter(u => u.id !== userToDelete.id));
                setDeleteModalOpen(false);
                setUserToDelete(null);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to delete user');
            }
        } catch (error) {
            toast.error('Error deleting user');
        } finally {
            setDeleting(false);
        }
    };

    const openResetPasswordModal = (user) => {
        setUserToReset(user);
        setNewPassword(generatePassword());
        setShowNewPassword(true);
        setResetPasswordModalOpen(true);
    };

    const handleResetPassword = async () => {
        if (!userToReset || !newPassword) return;
        
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setResetting(true);
        try {
            const response = await fetch(`${API}/admin/reset-password`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userToReset.id,
                    new_password: newPassword
                })
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(data.ghl_updated 
                    ? 'Password reset and GHL contact updated!' 
                    : 'Password reset successfully'
                );
                setResetPasswordModalOpen(false);
                setUserToReset(null);
                setNewPassword('');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to reset password');
            }
        } catch (error) {
            toast.error('Error resetting password');
        } finally {
            setResetting(false);
        }
    };

    const copyNewPassword = async () => {
        try {
            await navigator.clipboard.writeText(newPassword);
            toast.success('Password copied to clipboard');
        } catch (err) {
            // Fallback
            const textArea = document.createElement('textarea');
            textArea.value = newPassword;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success('Password copied to clipboard');
        }
    };

    const copyCredentials = async (userToCopy) => {
        const textToCopy = `Email: ${userToCopy.email}`;
        
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopiedId(userToCopy.id);
            setTimeout(() => setCopiedId(null), 2000);
            toast.success('Email copied to clipboard');
        } catch (err) {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = textToCopy;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedId(userToCopy.id);
                setTimeout(() => setCopiedId(null), 2000);
                toast.success('Email copied to clipboard');
            } catch (e) {
                toast.error('Failed to copy to clipboard');
            }
            document.body.removeChild(textArea);
        }
    };

    const roleColors = {
        admin: 'bg-[#F51E1E]/10 text-[#F51E1E] border-[#F51E1E]/30',
        reviewer: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
        submitter: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
        poster: 'bg-green-500/10 text-green-400 border-green-500/30'
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.05 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0 }
    };

    // Filter users based on search and role
    const filteredUsers = users.filter(user => {
        const matchesSearch = searchQuery === '' || 
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        
        return matchesSearch && matchesRole;
    });

    return (
        <Layout>
            <div className="max-w-5xl mx-auto" data-testid="admin-users-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <motion.h1 
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-3xl lg:text-4xl font-bold text-white mb-2"
                        >
                            Manage Users
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-[#A3A3A3]"
                        >
                            Add and manage platform users
                        </motion.p>
                    </div>

                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button 
                                className="bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full"
                                data-testid="add-user-btn"
                            >
                                <Plus size={18} className="mr-2" />
                                Add User
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#262626] border-[#404040] text-white">
                            <DialogHeader>
                                <DialogTitle>Create New User</DialogTitle>
                                <DialogDescription className="text-[#A3A3A3]">
                                    Add a new user to the platform. They will receive login credentials.
                                </DialogDescription>
                            </DialogHeader>

                            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-white">First Name</Label>
                                        <Input
                                            value={newUser.first_name}
                                            onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                                            placeholder="John"
                                            data-testid="new-user-firstname"
                                            className="mt-1 bg-[#1a1a1a] border-[#404040] text-white"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-white">Last Name</Label>
                                        <Input
                                            value={newUser.last_name}
                                            onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                                            placeholder="Doe"
                                            data-testid="new-user-lastname"
                                            className="mt-1 bg-[#1a1a1a] border-[#404040] text-white"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label className="text-white">Email</Label>
                                    <Input
                                        type="email"
                                        value={newUser.email}
                                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                        placeholder="john@example.com"
                                        data-testid="new-user-email"
                                        className="mt-1 bg-[#1a1a1a] border-[#404040] text-white"
                                    />
                                </div>

                                <div>
                                    <Label className="text-white">Password</Label>
                                    <Input
                                        type="text"
                                        value={newUser.password}
                                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                                        placeholder="Initial password"
                                        data-testid="new-user-password"
                                        className="mt-1 bg-[#1a1a1a] border-[#404040] text-white"
                                    />
                                </div>

                                <div>
                                    <Label className="text-white">Role</Label>
                                    <Select
                                        value={newUser.role}
                                        onValueChange={(value) => setNewUser({...newUser, role: value})}
                                    >
                                        <SelectTrigger 
                                            className="mt-1 bg-[#1a1a1a] border-[#404040] text-white"
                                            data-testid="new-user-role"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#262626] border-[#404040]">
                                            <SelectItem value="submitter">Submitter</SelectItem>
                                            <SelectItem value="reviewer">Reviewer</SelectItem>
                                            <SelectItem value="poster">Poster</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Company fields for Poster role */}
                                {newUser.role === 'poster' && (
                                    <div className="space-y-4 p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
                                        <p className="text-sm text-[#A3A3A3] flex items-center gap-2">
                                            <Building2 size={16} />
                                            Poster Company Details
                                        </p>
                                        <div>
                                            <Label className="text-white">Company Name</Label>
                                            <Input
                                                type="text"
                                                value={newUser.company_name}
                                                onChange={(e) => setNewUser({...newUser, company_name: e.target.value})}
                                                placeholder="Acme Productions"
                                                data-testid="new-user-company-name"
                                                className="mt-1 bg-[#262626] border-[#404040] text-white"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-white">Company Website (Optional)</Label>
                                            <Input
                                                type="url"
                                                value={newUser.company_website}
                                                onChange={(e) => setNewUser({...newUser, company_website: e.target.value})}
                                                placeholder="https://example.com"
                                                data-testid="new-user-company-website"
                                                className="mt-1 bg-[#262626] border-[#404040] text-white"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* User Tags for GHL */}
                                <div>
                                    <Label className="text-white flex items-center gap-2 mb-3">
                                        <Tag size={16} />
                                        User Type Tags (synced to GHL)
                                    </Label>
                                    <div className="space-y-3">
                                        {USER_TAG_OPTIONS.map((tag) => (
                                            <div 
                                                key={tag.id}
                                                className="flex items-center space-x-3"
                                            >
                                                <Checkbox
                                                    id={tag.id}
                                                    checked={newUser.user_tags.includes(tag.id)}
                                                    onCheckedChange={() => toggleTag(tag.id)}
                                                    className="border-[#404040] data-[state=checked]:bg-[#F51E1E] data-[state=checked]:border-[#F51E1E]"
                                                />
                                                <label
                                                    htmlFor={tag.id}
                                                    className="text-sm text-[#A3A3A3] cursor-pointer hover:text-white transition-colors"
                                                >
                                                    {tag.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-[#666] mt-2">
                                        Role ({newUser.role}) will also be added as a tag
                                    </p>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 border-[#404040] text-white hover:bg-[#404040]"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={creating}
                                        data-testid="create-user-btn"
                                        className="flex-1 bg-[#F51E1E] hover:bg-[#D41212] text-white"
                                    >
                                        {creating ? (
                                            <Loader2 className="animate-spin" size={18} />
                                        ) : (
                                            'Create User'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>

                    {/* Delete Confirmation Modal */}
                    <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                        <DialogContent className="bg-[#262626] border-[#404040] text-white max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                                        <Trash2 size={20} className="text-red-400" />
                                    </div>
                                    Delete User
                                </DialogTitle>
                                <DialogDescription className="text-[#A3A3A3] pt-4">
                                    Are you sure you want to delete this user? This action cannot be undone.
                                </DialogDescription>
                            </DialogHeader>

                            {userToDelete && (
                                <div className="bg-[#1a1a1a] rounded-xl p-4 my-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-[#262626] flex items-center justify-center">
                                            <span className="text-sm font-bold text-white">
                                                {userToDelete.first_name?.[0]}{userToDelete.last_name?.[0]}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-white">
                                                {userToDelete.first_name} {userToDelete.last_name}
                                            </p>
                                            <p className="text-sm text-[#A3A3A3]">{userToDelete.email}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setDeleteModalOpen(false);
                                        setUserToDelete(null);
                                    }}
                                    className="flex-1 border-[#404040] text-white hover:bg-[#404040]"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleDeleteUser}
                                    disabled={deleting}
                                    data-testid="confirm-delete-btn"
                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                >
                                    {deleting ? (
                                        <Loader2 className="animate-spin" size={18} />
                                    ) : (
                                        <>
                                            <Trash2 size={16} className="mr-2" />
                                            Delete User
                                        </>
                                    )}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Reset Password Modal */}
                    <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
                        <DialogContent className="bg-[#262626] border-[#404040] text-white max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                        <KeyRound size={20} className="text-amber-400" />
                                    </div>
                                    Reset Password
                                </DialogTitle>
                                <DialogDescription className="text-[#A3A3A3] pt-4">
                                    Set a new password for this user. The new password will be synced to GHL as the initial_password.
                                </DialogDescription>
                            </DialogHeader>

                            {userToReset && (
                                <div className="bg-[#1a1a1a] rounded-xl p-4 my-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-[#262626] flex items-center justify-center">
                                            <span className="text-sm font-bold text-white">
                                                {userToReset.first_name?.[0]}{userToReset.last_name?.[0]}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-white">
                                                {userToReset.first_name} {userToReset.last_name}
                                            </p>
                                            <p className="text-sm text-[#A3A3A3]">{userToReset.email}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div>
                                    <Label className="text-white">New Password</Label>
                                    <div className="flex gap-2 mt-1">
                                        <div className="relative flex-1">
                                            <Input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="Enter new password"
                                                data-testid="reset-password-input"
                                                className="h-11 bg-[#1a1a1a] border-[#404040] text-white pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                                            >
                                                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setNewPassword(generatePassword())}
                                            title="Generate new password"
                                            className="border-[#404040] text-white hover:bg-[#404040] px-3"
                                        >
                                            <RefreshCw size={16} />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={copyNewPassword}
                                            title="Copy password"
                                            className="border-[#404040] text-white hover:bg-[#404040] px-3"
                                        >
                                            <Copy size={16} />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-[#666] mt-2">
                                        Password will be updated in GHL as &#123;&#123; contact.initial_password &#125;&#125;
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setResetPasswordModalOpen(false);
                                        setUserToReset(null);
                                        setNewPassword('');
                                    }}
                                    className="flex-1 border-[#404040] text-white hover:bg-[#404040]"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleResetPassword}
                                    disabled={resetting || !newPassword || newPassword.length < 6}
                                    data-testid="confirm-reset-btn"
                                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                                >
                                    {resetting ? (
                                        <Loader2 className="animate-spin" size={18} />
                                    ) : (
                                        <>
                                            <KeyRound size={16} className="mr-2" />
                                            Reset Password
                                        </>
                                    )}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                        <Input
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 bg-[#262626] border-[#404040] text-white"
                            data-testid="user-search-input"
                        />
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-full sm:w-[180px] bg-[#262626] border-[#404040] text-white" data-testid="role-filter">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Filter by role" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#262626] border-[#404040]">
                            <SelectItem value="all">All Roles</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="poster">Poster</SelectItem>
                            <SelectItem value="submitter">Submitter</SelectItem>
                            <SelectItem value="reviewer">Reviewer</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Results count */}
                {(searchQuery || roleFilter !== 'all') && (
                    <p className="text-sm text-[#A3A3A3] mb-4">
                        Showing {filteredUsers.length} of {users.length} users
                        {searchQuery && <span> matching "{searchQuery}"</span>}
                        {roleFilter !== 'all' && <span> with role "{roleFilter}"</span>}
                    </p>
                )}

                {/* Users list */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-20 h-20 rounded-full bg-[#262626] flex items-center justify-center mb-4">
                            <Users size={36} className="text-[#A3A3A3]" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">
                            {users.length === 0 ? 'No users yet' : 'No users match your search'}
                        </h3>
                        <p className="text-[#A3A3A3]">
                            {users.length === 0 
                                ? 'Click "Add User" to create the first user.'
                                : 'Try adjusting your search or filter criteria.'
                            }
                        </p>
                        {(searchQuery || roleFilter !== 'all') && (
                            <Button
                                variant="outline"
                                onClick={() => { setSearchQuery(''); setRoleFilter('all'); }}
                                className="mt-4 border-[#404040] text-white hover:bg-[#404040]"
                            >
                                Clear Filters
                            </Button>
                        )}
                    </div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                        className="bg-[#262626] border border-[#404040] rounded-2xl overflow-x-auto"
                    >
                        <table className="w-full data-table table-fixed">
                            <thead className="bg-[#1a1a1a]">
                                <tr>
                                    <th style={{ width: '18%' }}>User</th>
                                    <th style={{ width: '32%' }}>Email</th>
                                    <th style={{ width: '12%' }}>Role</th>
                                    <th style={{ width: '13%' }}>Joined</th>
                                    <th style={{ width: '25%' }} className="text-right pr-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <motion.tr 
                                        key={user.id} 
                                        variants={itemVariants}
                                        data-testid={`user-row-${user.id}`}
                                    >
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                                                    <span className="text-sm font-bold text-white">
                                                        {user.first_name?.[0]}{user.last_name?.[0]}
                                                    </span>
                                                </div>
                                                <span className="font-medium">
                                                    {user.first_name} {user.last_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-[#A3A3A3]">
                                            <div className="flex items-center gap-2 truncate" title={user.email}>
                                                <Mail size={14} className="shrink-0" />
                                                <span className="truncate">{user.email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <Badge 
                                                variant="outline" 
                                                className={roleColors[user.role] || roleColors.submitter}
                                            >
                                                <Shield size={12} className="mr-1" />
                                                {user.role}
                                            </Badge>
                                        </td>
                                        <td className="text-[#A3A3A3]">
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => copyCredentials(user)}
                                                    title="Copy email"
                                                    className="text-[#A3A3A3] hover:text-white hover:bg-white/10 shrink-0"
                                                >
                                                    {copiedId === user.id ? (
                                                        <CheckCircle2 size={16} className="text-green-400" />
                                                    ) : (
                                                        <Copy size={16} />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openResetPasswordModal(user)}
                                                    title="Reset password"
                                                    data-testid={`reset-password-${user.id}`}
                                                    className="text-[#A3A3A3] hover:text-amber-400 hover:bg-amber-400/10 shrink-0"
                                                >
                                                    <KeyRound size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openDeleteModal(user)}
                                                    title="Delete user"
                                                    data-testid={`delete-user-${user.id}`}
                                                    className="text-[#A3A3A3] hover:text-red-400 hover:bg-red-400/10 shrink-0"
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
